import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ExpenditureLabelsProvider } from './contexts/ExpenditureLabelsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import AccessibilityWidget from './components/AccessibilityWidget';
import './i18n';

// Design Lab — isolated mockup gallery (no impact on real app)
const DesignLabIndex = React.lazy(() => import('./pages/DesignLab/DesignLabIndex'));
const DesignLabWorkbench = React.lazy(() => import('./pages/DesignLab/Workbench'));
const DesignLabBloomberg = React.lazy(() => import('./pages/DesignLab/Bloomberg'));
const DesignLabCanvas = React.lazy(() => import('./pages/DesignLab/Canvas'));
const DesignLabDocument = React.lazy(() => import('./pages/DesignLab/DocumentMode'));

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ActionDashboard from './pages/ActionDashboard';
import ApprovalsPage from './pages/ApprovalsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CategoryCodesPage from './pages/CategoryCodesPage';
import CurrencyCodesPage from './pages/CurrencyCodesPage';
import DeliveryPointsPage from './pages/DeliveryPointsPage';
import DocumentTypesPage from './pages/DocumentTypesPage';
import InvoicePointsPage from './pages/InvoicePointsPage';
import ItemsPage from './pages/ItemsPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierContractsPage from './pages/SupplierContractsPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import AccountCodesPage from './pages/AccountCodesPage';
import AccountCodeSetupPage from './pages/AccountCodeSetupPage';
import ContractSettingsPage from './pages/ContractSettingsPage';
import GoodsReceivedPage from './pages/GoodsReceivedPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import InventoryPage from './pages/InventoryPage';
import AssetManagement from './pages/AssetManagement';
import SupplierPortalPage from './pages/SupplierPortal';
import SupplierApprovalPage from './pages/SupplierApprovalPage';
import PublicTendersPage from './pages/PublicTendersPage';
import SystemSettingsPage from './pages/SystemSettingsPage';
import AuthSettingsPage from './pages/AuthSettingsPage';
import SelfOnboardingPage from './pages/SelfOnboardingPage';
import EyvoAdminPortal from './pages/EyvoAdminPortal';
import VersionHistoryPage from './pages/VersionHistoryPage';
import ProfilePage from './pages/ProfilePage';
import RequestsPage from './pages/RequestsPage';
import ATCPage from './pages/ATCPage';
import RequestDetailPage from './pages/RequestDetailPage';
import CustomersPage from './pages/CustomersPage';
import SalesOrdersPage from './pages/SalesOrdersPage';
import SalesOrderDetailPage from './pages/SalesOrderDetailPage';
import ReportsPage from './pages/ReportsPage';
import GroupItemsPage from './pages/GroupItemsPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import PurchaseOrderDetailPage from './pages/PurchaseOrderDetailPage';
import SystemAdminPage from './pages/SystemAdminPage';
import ExternalApprovalPage from './pages/ExternalApprovalPage';
import RFxPage from './pages/RFx';
import SourcingEventWizard from './pages/SourcingEventWizard';
import SavingsReportPage from './pages/SavingsReportPage';
import TraceabilityReportPage from './pages/TraceabilityReportPage';
import ScheduledReportsPage from './pages/Reports/ScheduledReportsPage';
import DocumentActivityLog from './pages/DocumentActivityLog';
import DataFilesReportsPage from './pages/DataFilesReportsPage';
import SourceProtection from './components/SourceProtection';

// User Guides
import GuideIndex from './pages/Guides/GuideIndex';
import GeneralSystemGuidePage from './pages/Guides/GeneralSystemGuide';
import DataFilesGuidePage from './pages/Guides/DataFilesGuide';
import AccountCodesGuidePage from './pages/Guides/AccountCodesGuide';
import ContractsGuidePage from './pages/Guides/ContractsGuide';
import RequisitionsGuidePage from './pages/Guides/RequisitionsGuide';
import { RfxSourcingGuidePage, PurchaseOrdersGuidePage, AssetRegisterGuidePage, InvoiceMatchingGuidePage, InventoryGuidePage, GoodsReceivingGuidePage, ReportsGuidePage } from './pages/Guides/ModuleGuides';

// Layouts
import AppShell from './components/layout/AppShell';
import AdminShell from './components/layout/AdminShell';
import { PlaceholderPage } from './components/layout/SharedComponents';
import { ClipboardList, ShoppingCart, FileQuestion, CheckSquare, Box, Receipt, Boxes, PackageCheck, BookOpen, CreditCard, Activity, Database, Bell, FileText } from 'lucide-react';

const Loading = () => <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
};

const RequireAdmin = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
};

const Placeholder = ({ title, icon }) => (
  <AppShell><PlaceholderPage title={title} icon={icon} /></AppShell>
);

const AdminPlaceholder = ({ title, icon }) => (
  <AdminShell><PlaceholderPage title={title} icon={icon} /></AdminShell>
);

function AppRoutes() {
  // Remove the Emergent platform badge after mount
  React.useEffect(() => {
    const remove = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) { badge.remove(); return true; }
      return false;
    };
    if (!remove()) {
      // Badge may not be injected yet — observe DOM for it
      const observer = new MutationObserver(() => { if (remove()) observer.disconnect(); });
      observer.observe(document.body, { childList: true, subtree: true });
      return () => observer.disconnect();
    }
  }, []);
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/onboard" element={<SelfOnboardingPage />} />
      <Route path="/eyvo-admin" element={<EyvoAdminPortal />} />
      <Route path="/approve-external" element={<ExternalApprovalPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Main app routes */}
      <Route path="/dashboard" element={<RequireAuth><ActionDashboard /></RequireAuth>} />
      <Route path="/data/categories" element={<RequireAuth><CategoryCodesPage /></RequireAuth>} />
      <Route path="/data/currencies" element={<RequireAuth><CurrencyCodesPage /></RequireAuth>} />
      <Route path="/data/delivery-points" element={<RequireAuth><DeliveryPointsPage /></RequireAuth>} />
      <Route path="/data/document-types" element={<RequireAuth><DocumentTypesPage /></RequireAuth>} />
      <Route path="/data/invoice-points" element={<RequireAuth><InvoicePointsPage /></RequireAuth>} />
      <Route path="/data/items" element={<RequireAuth><ItemsPage /></RequireAuth>} />
      <Route path="/data/suppliers" element={<RequireAuth><SuppliersPage /></RequireAuth>} />
      <Route path="/data/contracts" element={<RequireAuth><SupplierContractsPage /></RequireAuth>} />
      <Route path="/data/terms" element={<RequireAuth><TermsConditionsPage /></RequireAuth>} />
      <Route path="/data/group-items" element={<RequireAuth><GroupItemsPage /></RequireAuth>} />
      <Route path="/account-codes" element={<RequireAuth><AccountCodesPage /></RequireAuth>} />
      <Route path="/account-codes/:segmentNumber" element={<RequireAuth><AccountCodesPage /></RequireAuth>} />

      {/* Placeholder routes - Scheduled to be built */}
      <Route path="/requests" element={<RequireAuth><RequestsPage /></RequireAuth>} />
      <Route path="/atc" element={<RequireAuth><ATCPage /></RequireAuth>} />
      <Route path="/requests/:requestNumber" element={<RequireAuth><RequestDetailPage /></RequireAuth>} />
      <Route path="/customers" element={<RequireAuth><CustomersPage /></RequireAuth>} />
      <Route path="/sales-orders" element={<RequireAuth><SalesOrdersPage /></RequireAuth>} />
      <Route path="/sales-orders/:soNumber" element={<RequireAuth><SalesOrderDetailPage /></RequireAuth>} />
      <Route path="/orders" element={<RequireAuth><PurchaseOrdersPage /></RequireAuth>} />
      <Route path="/orders/:orderNumber" element={<RequireAuth><PurchaseOrderDetailPage /></RequireAuth>} />
      <Route path="/rfq" element={<RequireAuth><RFxPage /></RequireAuth>} />
      <Route path="/rfq/create" element={<RequireAuth><SourcingEventWizard /></RequireAuth>} />
      <Route path="/approvals" element={<RequireAuth><ApprovalsPage /></RequireAuth>} />
      <Route path="/assets" element={<RequireAuth><AssetManagement /></RequireAuth>} />
      <Route path="/invoice-matching" element={<RequireAuth><Placeholder title="Invoice Matching" icon={Receipt} /></RequireAuth>} />
      <Route path="/inventory" element={<RequireAuth><InventoryPage /></RequireAuth>} />
      <Route path="/goods-received" element={<RequireAuth><GoodsReceivedPage /></RequireAuth>} />
      <Route path="/invoices" element={<RequireAuth><InvoicesPage /></RequireAuth>} />
      <Route path="/invoices/:invoiceRef" element={<RequireAuth><InvoiceDetailPage /></RequireAuth>} />
      <Route path="/recipe" element={<RequireAuth><Placeholder title="Bill of Materials / Recipe" icon={BookOpen} /></RequireAuth>} />
      <Route path="/expenses" element={<RequireAuth><Placeholder title="Expense Management" icon={CreditCard} /></RequireAuth>} />
      <Route path="/supplier-portal" element={<SupplierPortalPage />} />
      <Route path="/approve-supplier/:token" element={<SupplierApprovalPage />} />
      <Route path="/public_RFX" element={<PublicTendersPage />} />
      <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
      <Route path="/scheduled-reports" element={<RequireAuth><ScheduledReportsPage /></RequireAuth>} />
      <Route path="/activity-log" element={<RequireAuth><DocumentActivityLog /></RequireAuth>} />
      <Route path="/data/reports" element={<RequireAuth><DataFilesReportsPage /></RequireAuth>} />

      {/* Admin routes */}
      <Route path="/admin/users" element={<Navigate to="/system-admin" replace />} />
      <Route path="/admin/users/new" element={<Navigate to="/system-admin" replace />} />
      <Route path="/admin/users/:userId" element={<Navigate to="/system-admin" replace />} />
      <Route path="/system-admin" element={<RequireAdmin><SystemAdminPage /></RequireAdmin>} />
      <Route path="/system-admin/*" element={<RequireAdmin><SystemAdminPage /></RequireAdmin>} />
      
      {/* Reports */}
      <Route path="/reports" element={<RequireAuth><ReportsPage /></RequireAuth>} />
      <Route path="/reports/savings" element={<RequireAuth><SavingsReportPage /></RequireAuth>} />
      <Route path="/reports/traceability" element={<RequireAuth><TraceabilityReportPage /></RequireAuth>} />
      <Route path="/admin/audit-logs" element={<RequireAdmin><AdminPlaceholder title="Audit Logs" icon={Activity} /></RequireAdmin>} />
      <Route path="/admin/account-code-setup" element={<RequireAdmin><AccountCodeSetupPage /></RequireAdmin>} />
      <Route path="/admin/contract-settings" element={<RequireAdmin><ContractSettingsPage /></RequireAdmin>} />
      <Route path="/admin/system-settings" element={<RequireAdmin><SystemSettingsPage /></RequireAdmin>} />
      <Route path="/admin/auth-settings" element={<RequireAdmin><AuthSettingsPage /></RequireAdmin>} />
      <Route path="/admin/version-history" element={<RequireAdmin><VersionHistoryPage /></RequireAdmin>} />
      <Route path="/admin/settings" element={<RequireAdmin><AdminPlaceholder title="Environment Settings" icon={Database} /></RequireAdmin>} />
      <Route path="/admin/notifications" element={<RequireAdmin><AdminPlaceholder title="System Notifications" icon={Bell} /></RequireAdmin>} />
      <Route path="/admin/*" element={<RequireAdmin><AdminPlaceholder title="Admin Module" icon={Database} /></RequireAdmin>} />

      {/* User Guides */}
      <Route path="/guides" element={<RequireAuth><GuideIndex /></RequireAuth>} />
      <Route path="/guides/general" element={<RequireAuth><GeneralSystemGuidePage /></RequireAuth>} />
      <Route path="/guides/data-files" element={<RequireAuth><DataFilesGuidePage /></RequireAuth>} />
      <Route path="/guides/account-codes" element={<RequireAuth><AccountCodesGuidePage /></RequireAuth>} />
      <Route path="/guides/contracts" element={<RequireAuth><ContractsGuidePage /></RequireAuth>} />
      <Route path="/guides/requisitions" element={<RequireAuth><RequisitionsGuidePage /></RequireAuth>} />
      <Route path="/guides/rfx-sourcing" element={<RequireAuth><RfxSourcingGuidePage /></RequireAuth>} />
      <Route path="/guides/purchase-orders" element={<RequireAuth><PurchaseOrdersGuidePage /></RequireAuth>} />
      <Route path="/guides/asset-register" element={<RequireAuth><AssetRegisterGuidePage /></RequireAuth>} />
      <Route path="/guides/invoice-matching" element={<RequireAuth><InvoiceMatchingGuidePage /></RequireAuth>} />
      <Route path="/guides/inventory" element={<RequireAuth><InventoryGuidePage /></RequireAuth>} />
      <Route path="/guides/goods-receiving" element={<RequireAuth><GoodsReceivingGuidePage /></RequireAuth>} />
      <Route path="/guides/reports" element={<RequireAuth><ReportsGuidePage /></RequireAuth>} />

      {/* Design Lab — public mockups (no auth, no live data) */}
      <Route path="/design-lab" element={<Suspense fallback={null}><DesignLabIndex /></Suspense>} />
      <Route path="/design-lab/workbench" element={<Suspense fallback={null}><DesignLabWorkbench /></Suspense>} />
      <Route path="/design-lab/bloomberg" element={<Suspense fallback={null}><DesignLabBloomberg /></Suspense>} />
      <Route path="/design-lab/canvas" element={<Suspense fallback={null}><DesignLabCanvas /></Suspense>} />
      <Route path="/design-lab/document" element={<Suspense fallback={null}><DesignLabDocument /></Suspense>} />

      {/* 404 */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

import { ConfirmProvider } from './components/ConfirmDialog';

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <ExpenditureLabelsProvider>
            <ConfirmProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
              <AccessibilityWidget />
              <SourceProtection />
            </ConfirmProvider>
          </ExpenditureLabelsProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
